import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import {
  GraduationCap, Target, BookOpen, Award, Rocket, DollarSign,
  Zap, ArrowRight, Plus, Settings2, X, Compass, TrendingUp,
  Star, Users, BarChart3, CheckCircle2, ChevronRight,
  Briefcase, Building, Shield, Calendar, User, GripVertical, Sparkles, Brain, Circle
} from 'lucide-react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };

/* ── Journey config ──────────────────────────────────────────────────────── */
const JOURNEY_STEPS = [
  { key: 'self_discovery', icon: Compass, label: 'Self-Discovery', link: '/app/assessment', color: '#39E3DC' },
  { key: 'career_reality', icon: TrendingUp, label: 'Career Reality', link: '/app/career-intelligence', color: '#3b82f6' },
  { key: 'build_proof', icon: Award, label: 'Build Proof', link: '/app/proof-builder', color: '#782FE1' },
  { key: 'college_prep', icon: GraduationCap, label: 'College Prep', link: '/app/college-intel', color: '#f59e0b' },
  { key: 'experience', icon: Rocket, label: 'Experience', link: '/app/internships', color: '#f43f5e' },
  { key: 'financial', icon: DollarSign, label: 'Financial', link: '/app/financial', color: '#15BA89' },
];

/* ── Module definitions ──────────────────────────────────────────────────── */
const ALL_MODULES = {
  journey: { type: 'journey', label: 'Your Journey', removable: false },
  cdp_plan: { type: 'cdp_plan', label: 'Career Development Plan', removable: false },
  cte_pathway: { type: 'cte_pathway', label: 'CTE Pathway', removable: false },
  nc_cdp: { type: 'nc_cdp', label: 'District Plan', removable: true },
  financial: { type: 'financial', label: 'Financial Outlook', removable: true },
  ai_impact: { type: 'ai_impact', label: 'AI Career Impact', removable: true },
  proof: { type: 'proof', label: 'Proof Challenges', removable: true },
  next_moves: { type: 'next_moves', label: 'Your Next Moves', removable: true },
  readiness: { type: 'readiness', label: 'Career Readiness', removable: true },
  lifetime_earnings: { type: 'lifetime_earnings', label: 'Lifetime Earnings', removable: true },
  career_landscape: { type: 'career_landscape', label: 'Career Landscape', removable: true },
  assessment_summary: { type: 'assessment_summary', label: 'Assessment Summary', removable: true },
};

const DEFAULT_MODULES = [
  { id: 'journey', type: 'journey', label: 'Your Journey', visible: true, removable: false },
  { id: 'cdp_plan', type: 'cdp_plan', label: 'Career Development Plan', visible: true, removable: false },
  { id: 'cte_pathway', type: 'cte_pathway', label: 'CTE Pathway', visible: true, removable: false },
  { id: 'financial', type: 'financial', label: 'Financial Intelligence', visible: true, removable: true },
  { id: 'next_moves', type: 'next_moves', label: 'Your Next Moves', visible: true, removable: true },
  { id: 'readiness', type: 'readiness', label: 'Career Readiness', visible: true, removable: true },
  { id: 'ai_impact', type: 'ai_impact', label: 'AI Career Impact', visible: true, removable: true },
  { id: 'proof', type: 'proof', label: 'Proof Challenges', visible: true, removable: true },
  { id: 'lifetime_earnings', type: 'lifetime_earnings', label: 'Lifetime Earnings', visible: true, removable: true },
  { id: 'career_landscape', type: 'career_landscape', label: 'Career Landscape', visible: true, removable: true },
  { id: 'assessment_summary', type: 'assessment_summary', label: 'Assessment Summary', visible: true, removable: true },
];

/* ═════════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═════════════════════════════════════════════════════════════════════════════ */
export default function HSDashboardPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isCollege = !!user && (user.student_type === 'college' || user.welcome_archetype === 'college_student');
  const [data, setData] = useState(null);
  const [proofData, setProofData] = useState(null);
  const [ncCdp, setNcCdp] = useState(null);
  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);

  // Gen2 college students never see the high-school (CDP/CTE/district) dashboard.
  useEffect(() => {
    if (isCollege) navigate('/app/college-dashboard', { replace: true });
  }, [isCollege, navigate]);

  useEffect(() => {
    if (!token || isCollege) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/college/hs-dashboard`, h),
      axios.get(`${API}/college/proof-builder`, h).catch(() => ({ data: null })),
      axios.get(`${API}/college/dashboard-config`, h).catch(() => ({ data: null })),
    ])
      .then(([dash, proof, config]) => {
        setData(dash.data);
        setProofData(proof.data);
        setNcCdp(dash.data?.nc_cdp || null);
        // Load HS config: check for hs_modules first, then check if saved modules are HS-type
        const saved = config.data?.hs_modules || config.data?.modules || [];
        const isHsConfig = saved.some(m => ['nc_cdp', 'readiness', 'proof', 'ai_impact', 'cdp_plan', 'cte_pathway'].includes(m.id));
        let finalMods = (isHsConfig && saved.length >= 3) ? [...saved] : [...DEFAULT_MODULES];
        // Always ensure pinned modules exist (CDP Plan + CTE Pathway)
        const REQUIRED = [
          { id: 'cdp_plan', type: 'cdp_plan', label: 'Career Development Plan', visible: true, removable: false },
          { id: 'cte_pathway', type: 'cte_pathway', label: 'CTE Pathway', visible: true, removable: false },
        ];
        for (const req of REQUIRED) {
          if (!finalMods.some(m => m.id === req.id)) {
            // Insert after 'journey' (position 1) or at start
            const journeyIdx = finalMods.findIndex(m => m.id === 'journey');
            finalMods.splice(journeyIdx + 1, 0, req);
          } else {
            // Ensure it's visible and non-removable
            finalMods = finalMods.map(m => m.id === req.id ? { ...m, visible: true, removable: false } : m);
          }
        }
        // Remove old nc_cdp module since cdp_plan replaces it
        finalMods = finalMods.filter(m => m.id !== 'nc_cdp');
        // Ensure all default modules exist (add any missing ones at the end)
        for (const def of DEFAULT_MODULES) {
          if (!finalMods.some(m => m.id === def.id)) {
            finalMods.push({ ...def });
          }
        }
        setModules(finalMods);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const saveConfig = useCallback(async (newMods) => {
    setModules(newMods);
    try { await axios.post(`${API}/college/dashboard-config`, { modules: newMods, hs_modules: newMods }, { headers: { Authorization: `Bearer ${token}` } }); } catch {}
  }, [token]);

  const removeModule = useCallback((id) => saveConfig(modules.map(m => m.id === id ? { ...m, visible: false } : m)), [modules, saveConfig]);
  const restoreModule = useCallback((id) => saveConfig(modules.map(m => m.id === id ? { ...m, visible: true } : m)), [modules, saveConfig]);
  const addModule = useCallback((id) => {
    const def = ALL_MODULES[id];
    if (!def) return;
    saveConfig([...modules, { id, type: def.type, label: def.label, visible: true, removable: def.removable ?? true }]);
  }, [modules, saveConfig]);

  if (isCollege || loading) return <AppLayout><div className="flex items-center justify-center min-h-[60vh]"><LoadingSpinner size="lg" /></div></AppLayout>;
  if (!data) return <AppLayout><div className="text-center py-20 text-[#C6D4E8]">Unable to load dashboard.</div></AppLayout>;

  const { student, stage, radar, overall_readiness, next_actions, truth_statement, ai_impact, journey, current_path, cte_pathway } = data;
  const visibleModules = modules.filter(m => m.visible);
  const hiddenModules = modules.filter(m => !m.visible);
  const addableModules = Object.entries(ALL_MODULES).filter(([id]) => !modules.some(m => m.id === id && m.visible));

  const renderModule = (mod) => {
    switch (mod.type || mod.id) {
      case 'journey': return <JourneyPipeline journey={journey} path={current_path} />;
      case 'cdp_plan': return ncCdp ? <CDPPlanModule cdp={ncCdp} /> : null;
      case 'cte_pathway': return cte_pathway ? <CTEPathwayModule cte={cte_pathway} target={student.career_target} /> : null;
      case 'nc_cdp': return ncCdp?.is_institutional ? <NCCDPModule cdp={ncCdp} /> : null;
      case 'financial': return <FinancialModule />;
      case 'ai_impact': return ai_impact?.available ? <AIImpactModule impact={ai_impact} /> : null;
      case 'proof': return proofData?.proof_items?.length ? <ProofModule proof={proofData} /> : null;
      case 'next_moves': return next_actions?.length ? <NextMovesModule actions={next_actions} /> : null;
      case 'readiness': return radar?.length ? <ReadinessModule radar={radar} overall={overall_readiness} /> : null;
      case 'lifetime_earnings': return <LifetimeEarningsModule />;
      case 'career_landscape': return <CareerLandscapeModule target={student.career_target} />;
      case 'assessment_summary': return <AssessmentSummaryModule />;
      default: return null;
    }
  };

  return (
    <AppLayout>
      <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-5 max-w-[1400px] mx-auto px-3 sm:px-6 pb-8" data-testid="hs-dashboard">

        {/* ═══ HERO ═══ */}
        <motion.div variants={fadeUp}>
          <div className="relative overflow-hidden rounded-[18px] p-6 sm:p-7 md:p-8"
            style={{ background: 'rgba(6, 23, 54, 0.72)', backdropFilter: 'blur(16px)', border: '1px solid rgba(57, 227, 220, 0.28)',
              boxShadow: '0 0 8px rgba(57,227,220,0.65), 0 0 20px rgba(57,227,220,0.30), 0 0 42px rgba(57,227,220,0.12)' }} data-testid="hs-dash-hero">
            <div aria-hidden className="pointer-events-none absolute -top-28 -right-20 w-[26rem] h-[26rem] rounded-full blur-3xl opacity-30"
              style={{ background: 'radial-gradient(circle, #39E3DC, transparent 65%)' }} />
            <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-16 w-[22rem] h-[22rem] rounded-full blur-3xl opacity-20"
              style={{ background: 'radial-gradient(circle, #782FE1, transparent 65%)' }} />
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(57,227,220,0.55), transparent)' }} />

            <div className="relative">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border border-cyan-400/35 bg-cyan-400/[0.08] text-[#39E3DC]"
                  style={{ boxShadow: '0 0 10px rgba(57,227,220,0.2)' }}>{stage?.stage_label || 'Building Your Foundation'}</span>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border border-violet-400/30 bg-violet-400/[0.08] text-[#a78bfa]">Grade {student.grade_level}</span>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border border-cyan-400/25 bg-cyan-400/[0.06] text-[#39E3DC]/80">{student.school}</span>
                {student.counselor && (
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border border-emerald-400/25 bg-emerald-400/[0.06] text-[#15BA89]/80">Counselor: {student.counselor}</span>
                )}
              </div>
              <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mt-3"
                style={{ textShadow: '0 0 60px rgba(57,227,220,0.2)' }}>Welcome back, {student.first_name}.</h1>
              <p className="text-base text-slate-300/90 mt-3 max-w-2xl">{truth_statement}</p>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="hs-dash-stats">
                {[
                  { label: 'Grade', value: student.grade_level, icon: GraduationCap, link: '/app/start-here' },
                  { label: 'GPA', value: student.gpa || '-', icon: BarChart3, link: '/app/start-here' },
                  { label: 'School', value: student.school, icon: Building, link: '/app/start-here' },
                  { label: 'Graduation', value: student.graduation_year, icon: Calendar, link: '/app/goals' },
                  { label: 'Career Target', value: student.career_target, icon: Target, link: '/app/career-intelligence', accent: true },
                  { label: 'Counselor', value: student.counselor || 'Not assigned', icon: Users, link: '/app/counselor-meetings' },
                ].map((t, i) => (
                  <Link key={i} to={t.link || '#'}
                    className="block rounded-xl border p-3 transition-all duration-300 group hover:scale-[1.02] hover:-translate-y-0.5 hover:border-cyan-400/60"
                    style={{
                      borderColor: t.accent ? 'rgba(57,227,220,0.40)' : 'rgba(57,227,220,0.18)',
                      background: t.accent ? 'linear-gradient(135deg, rgba(57,227,220,0.10), rgba(57,227,220,0.04))' : 'linear-gradient(135deg, rgba(57,227,220,0.05), transparent)',
                      boxShadow: t.accent ? '0 0 24px -4px rgba(57,227,220,0.3)' : '0 0 16px -4px rgba(57,227,220,0.15)',
                    }}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <t.icon className="w-3.5 h-3.5 text-[#39E3DC] flex-shrink-0" style={{ filter: 'drop-shadow(0 0 4px rgba(57,227,220,0.5))' }} />
                      <p className="text-[9px] uppercase tracking-wider text-[#39E3DC] font-bold leading-none">{t.label}</p>
                    </div>
                    <p className="text-sm font-bold leading-tight text-white group-hover:text-[#39E3DC] transition-colors truncate">{t.value}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* ═══ MODULE CONTROLS ═══ */}
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => setShowAddPanel(!showAddPanel)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium text-[#39E3DC] border border-cyan-500/20 bg-cyan-500/[0.05] hover:bg-cyan-500/[0.1] transition-all"
            data-testid="add-module-btn"><Plus className="w-3 h-3" /> Add Module</button>
          <button onClick={() => setEditMode(!editMode)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
              editMode ? 'text-[#ECC451] border-amber-500/30 bg-amber-500/[0.1]' : 'text-slate-300 border-white/[0.06] hover:border-white/[0.12]'}`}
            data-testid="edit-dashboard-btn"><Settings2 className="w-3 h-3" /> {editMode ? 'Done' : 'Customize'}</button>
        </div>

        {/* ═══ ADD MODULE PANEL ═══ */}
        <AnimatePresence>
          {showAddPanel && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
              <div className="rounded-2xl border border-[rgba(89,126,190,0.22)] bg-[#061736] p-5 mb-2">
                <p className="text-xs font-bold text-[#39E3DC] uppercase tracking-wider mb-3">Add a module to your dashboard</p>
                <div className="flex flex-wrap gap-2">
                  {addableModules.map(([id, mod]) => (
                    <button key={id} onClick={() => { addModule(id); setShowAddPanel(false); }}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-white border border-[rgba(57,227,220,0.22)] bg-cyan-500/[0.06] hover:bg-cyan-500/[0.12] hover:border-cyan-400/40 transition-all"
                      data-testid={`add-module-${id}`}><Plus className="w-3 h-3 text-[#39E3DC]" /> {mod.label}</button>
                  ))}
                  {hiddenModules.map(mod => (
                    <button key={mod.id} onClick={() => restoreModule(mod.id)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-slate-300 border border-white/[0.06] hover:border-[rgba(57,227,220,0.22)] hover:text-white transition-all">
                      <Plus className="w-3 h-3" /> {mod.label} (restore)</button>
                  ))}
                  {addableModules.length === 0 && hiddenModules.length === 0 && (
                    <p className="text-xs text-slate-300">All available modules are on your dashboard.</p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ═══ DRAGGABLE MODULES, Reorder.Group like college ═══ */}
        <Reorder.Group axis="y" values={visibleModules}
          onReorder={(newOrder) => {
            const hiddenOnes = modules.filter(m => !m.visible);
            saveConfig([...newOrder, ...hiddenOnes]);
          }} className="space-y-5">
          {visibleModules.map((mod) => {
            const content = renderModule(mod);
            if (!content) return null;
            return (
              <Reorder.Item key={mod.id} value={mod} dragListener={editMode}>
                <motion.div variants={fadeUp} className="relative group">
                  {editMode && (
                    <div className="absolute -left-2 top-1/2 -translate-y-1/2 z-10">
                      <div className="w-6 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center cursor-grab active:cursor-grabbing">
                        <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                      </div>
                    </div>
                  )}
                  {editMode && mod.removable !== false && (
                    <button onClick={() => removeModule(mod.id)}
                      className="absolute -right-2 -top-2 z-10 w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center hover:bg-red-500/40 transition-all"
                      data-testid={`remove-module-${mod.id}`}><X className="w-3 h-3 text-red-400" /></button>
                  )}
                  <div className={editMode ? 'ring-1 ring-cyan-500/20 rounded-2xl' : ''}>{content}</div>
                </motion.div>
              </Reorder.Item>
            );
          })}
        </Reorder.Group>

        {/* Stage context */}
        <motion.div variants={fadeUp}>
          <div className="rounded-2xl border border-white/[0.07] bg-[#030C1D] p-4" data-testid="hs-dash-stage-detail">
            <div className="flex items-start gap-3">
              <Sparkles className="w-4 h-4 text-[#39E3DC] flex-shrink-0 mt-0.5" />
              <p className="text-slate-300 text-sm leading-relaxed">{stage?.description || truth_statement}</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AppLayout>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   JOURNEY, EXACT same pattern as college
   ═══════════════════════════════════════════════════════════════════════════ */
function JourneyPipeline({ journey, path }) {
  const s = journey || {};
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(57,227,220,0.22)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 6px rgba(57,227,220,0.50), 0 0 16px rgba(57,227,220,0.22), 0 0 36px rgba(57,227,220,0.09)' }} data-testid="journey-pipeline">
      <div aria-hidden className="pointer-events-none absolute -top-10 left-1/3 w-40 h-40 rounded-full blur-3xl opacity-10"
        style={{ background: 'radial-gradient(circle, #39E3DC, transparent 65%)' }} />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#39E3DC]" style={{ textShadow: '0 0 8px rgba(57,227,220,0.3)' }}>Your Journey</p>
          <p className="text-[10px] text-slate-300">{path?.transition || 'High School → Career'}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {JOURNEY_STEPS.map((step, i) => {
            const Icon = step.icon;
            const pct = s[step.key] || 0;
            const isActive = pct > 0;
            return (
              <motion.div key={step.key} className="flex-1"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}>
                <Link to={step.link} className="block group" data-testid={`journey-step-${step.key}`}>
                  <div className="relative rounded-xl border p-3 text-center transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.03]"
                    style={{
                      borderColor: isActive ? `${step.color}40` : 'rgba(255,255,255,0.06)',
                      backgroundColor: isActive ? `${step.color}08` : 'rgba(255,255,255,0.02)',
                      boxShadow: isActive ? `0 0 24px -6px ${step.color}55` : 'none',
                    }}>
                    {/* Animated fill */}
                    <div className="absolute inset-0 rounded-xl overflow-hidden">
                      <motion.div className="h-full rounded-xl"
                        initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                        transition={{ duration: 1, delay: 0.3 + i * 0.1, ease: 'easeOut' }}
                        style={{ background: step.color, opacity: 0.12 }} />
                    </div>
                    <div className="relative">
                      <Icon className="w-4 h-4 mx-auto mb-1 transition-colors"
                        style={{ color: isActive ? step.color : '#C6D4E8', filter: isActive ? `drop-shadow(0 0 6px ${step.color}88)` : 'none' }} />
                      {pct === 100 && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5 + i * 0.1, type: 'spring' }}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#15BA89] flex items-center justify-center" style={{ boxShadow: '0 0 8px rgba(21,186,137,0.6)' }}>
                        <CheckCircle2 className="w-3 h-3 text-white" /></motion.div>}
                      <p className={`text-[10px] font-medium leading-tight transition-colors ${isActive ? 'text-slate-200' : 'text-slate-300 group-hover:text-slate-300'}`}>{step.label}</p>
                      <p className="text-base font-outfit font-black mt-0.5"
                        style={{ color: step.color, textShadow: `0 0 14px ${step.color}66, 0 0 30px ${step.color}22` }}>{pct}%</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ═══ NC CDP ═══ */
function NCCDPModule({ cdp }) {
  const reqs = [
    { label: 'Career Assessment', key: 'has_assessment', icon: Compass },
    { label: 'Career Goal', key: 'career_goal', icon: Target },
    { label: 'CTE Pathway', key: 'cte_pathway', icon: Shield },
    { label: 'Course Plan', key: 'has_course_plan', icon: BookOpen },
    { label: 'WBL Hours', key: 'wbl_hours', icon: Briefcase, fmt: (v) => `${v}h of ${cdp.wbl_goal || 40}h` },
    { label: 'CDP Progress', key: 'sections_complete', icon: BarChart3, fmt: (v) => `${v} of ${cdp.total_sections || 9} sections` },
  ];
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(120,47,225,0.22)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 6px rgba(120,47,225,0.30), 0 0 18px rgba(120,47,225,0.10)' }}>
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(120,47,225,0.45), transparent)' }} />
      <div className="flex items-center justify-between mb-1">
        <div>
          <p className="text-[9px] text-slate-300 uppercase tracking-wider font-semibold">{cdp.district}</p>
          <h2 className="text-base sm:text-lg font-bold text-white">{cdp.school_name} <span className="text-slate-300 font-normal text-sm">Career Development Plan</span></h2>
        </div>
        <div className="text-right">
          <span className="text-3xl sm:text-4xl font-extrabold text-white" style={{ textShadow: '0 0 20px rgba(120,47,225,0.3)' }}>{cdp.overall_completion}%</span>
          <div className="text-[9px] text-slate-300">{cdp.sections_complete} of {cdp.total_sections} complete</div>
        </div>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden mb-4" style={{ background: 'rgba(120,47,225,0.1)' }}>
        <div className="h-full rounded-full" style={{ width: `${cdp.overall_completion}%`, background: 'linear-gradient(90deg, #782FE1, #39E3DC)', boxShadow: '0 0 10px rgba(120,47,225,0.5)' }} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {reqs.map(r => {
          const val = cdp[r.key];
          const done = val && val !== false && val !== 0;
          return (
            <div key={r.label} className="flex items-center gap-3 p-2.5 rounded-lg"
              style={{ background: done ? 'rgba(21,186,137,0.04)' : 'rgba(255,255,255,0.02)', border: `1px solid ${done ? 'rgba(21,186,137,0.2)' : 'rgba(255,255,255,0.05)'}` }}>
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${done ? 'bg-emerald-500/[0.12]' : 'bg-slate-500/[0.08]'}`}>
                {done ? <CheckCircle2 className="w-4 h-4 text-[#15BA89]" style={{ filter: 'drop-shadow(0 0 4px rgba(21,186,137,0.5))' }} /> : <r.icon className="w-3.5 h-3.5 text-slate-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-semibold ${done ? 'text-white' : 'text-slate-300'}`}>{r.label}</div>
                <div className="text-[10px] text-slate-300 truncate">{r.fmt ? r.fmt(val) : (typeof val === 'string' ? val : (done ? 'Complete' : 'Not started'))}</div>
              </div>
            </div>
          );
        })}
      </div>
      {cdp.counselor_name && (
        <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-300">
          <User className="w-3 h-3" /> Counselor: <span className="text-white font-medium">{cdp.counselor_name}</span>
          <span>reviews your progress each semester</span>
        </div>
      )}
    </div>
  );
}

/* ═══ CDP PLAN (Career Development Plan - Prominent, Pinned) ═══ */
function CDPPlanModule({ cdp }) {
  const compliancePct = cdp.compliance_pct || Math.min(100, Math.round((cdp.sections_complete / Math.max(cdp.total_sections, 1)) * 100 + 20));
  const remaining = cdp.compliance_remaining || [];
  return (
    <div className="relative overflow-hidden rounded-2xl p-5 sm:p-6" data-testid="cdp-plan-module"
      style={{ background: 'rgba(6,23,54,0.72)', backdropFilter: 'blur(16px)', border: '1px solid rgba(120,47,225,0.30)',
        boxShadow: '0 0 8px rgba(120,47,225,0.50), 0 0 22px rgba(120,47,225,0.18)' }}>
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #782FE1, transparent)' }} />
      <div aria-hidden className="pointer-events-none absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-[0.06]" style={{ background: 'radial-gradient(circle, #782FE1, transparent 65%)' }} />

      <div className="relative">
        {/* Header with completion */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(120,47,225,0.15)', border: '1px solid rgba(120,47,225,0.3)', boxShadow: '0 0 14px rgba(120,47,225,0.2)' }}>
              <Target className="w-5 h-5 text-[#a78bfa]" style={{ filter: 'drop-shadow(0 0 5px rgba(120,47,225,0.6))' }} />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">Career Development Plan</h2>
              <p className="text-[10px] text-[#C6D4E8]">{cdp.school_name || 'High School'} {cdp.district ? `· ${cdp.district}` : ''}</p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-3xl font-extrabold text-white" style={{ textShadow: '0 0 22px rgba(120,47,225,0.4)' }}>{cdp.overall_completion}%</span>
            <div className="text-[9px] text-[#C6D4E8] font-medium">Plan completion</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2 rounded-full overflow-hidden mb-4" style={{ background: 'rgba(120,47,225,0.08)' }}>
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${cdp.overall_completion}%`, background: 'linear-gradient(90deg, #782FE1, #39E3DC)', boxShadow: '0 0 12px rgba(120,47,225,0.5)' }} />
        </div>

        {/* Key message */}
        {cdp.cdp_message && (
          <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(120,47,225,0.06)', border: '1px solid rgba(120,47,225,0.18)' }}>
            <p className="text-xs text-[#8fa3c0] leading-relaxed italic">{cdp.cdp_message}</p>
          </div>
        )}

        {/* Stats row - clickable */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Link to="/app/my-cdp" className="p-3 rounded-lg text-center transition-all hover:-translate-y-0.5 hover:shadow-[0_0_16px_rgba(57,227,220,0.15)]" style={{ background: 'rgba(57,227,220,0.04)', border: '1px solid rgba(57,227,220,0.12)' }}>
            <div className="text-sm font-bold text-[#39E3DC] leading-tight" style={{ filter: 'drop-shadow(0 0 6px rgba(57,227,220,0.4))' }}>{cdp.career_goal || 'Exploring'}</div>
            <div className="text-[9px] text-[#C6D4E8] font-medium mt-0.5">Career Direction</div>
          </Link>
          <Link to="/app/my-cdp" className="p-3 rounded-lg text-center transition-all hover:-translate-y-0.5 hover:shadow-[0_0_16px_rgba(21,186,137,0.15)]" style={{ background: 'rgba(21,186,137,0.04)', border: '1px solid rgba(21,186,137,0.12)' }}>
            <div className="text-lg font-bold text-[#15BA89]" style={{ filter: 'drop-shadow(0 0 6px rgba(21,186,137,0.4))' }}>{compliancePct}%</div>
            <div className="text-[9px] text-[#C6D4E8] font-medium mt-0.5">State Compliance</div>
          </Link>
          <Link to="/app/nc-wbl" className="p-3 rounded-lg text-center transition-all hover:-translate-y-0.5 hover:shadow-[0_0_16px_rgba(236,196,81,0.15)]" style={{ background: 'rgba(236,196,81,0.04)', border: '1px solid rgba(236,196,81,0.12)' }}>
            <div className="text-lg font-bold text-[#ECC451]" style={{ filter: 'drop-shadow(0 0 6px rgba(236,196,81,0.4))' }}>{cdp.wbl_hours || 0}h</div>
            <div className="text-[9px] text-[#C6D4E8] font-medium mt-0.5">WBL of {cdp.wbl_goal || 40}h</div>
          </Link>
        </div>

        {/* Remaining items - clickable */}
        {remaining.length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] text-[#ECC451] font-semibold uppercase tracking-wider mb-2">Action Needed</div>
            {remaining.map((item, i) => (
              <Link key={i} to="/app/proof-builder" className="flex items-center gap-2 py-1.5 text-xs group hover:text-[#39E3DC] transition-colors">
                <div className="w-1.5 h-1.5 rounded-full bg-[#ECC451]" style={{ boxShadow: '0 0 6px rgba(236,196,81,0.6)' }} />
                <span className="text-white font-medium group-hover:text-[#39E3DC]">{item}</span>
                <ArrowRight className="w-3 h-3 text-[#C6D4E8] group-hover:text-[#39E3DC] ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        )}

        {/* CDP History */}
        {cdp.history?.length > 0 && (
          <div>
            <div className="text-[10px] text-[#C6D4E8] font-semibold uppercase tracking-wider mb-2">CDP History</div>
            <div className="space-y-1.5">
              {cdp.history.map((h, i) => (
                <div key={i} className="flex items-start gap-2 text-[10px]">
                  <span className="text-[#C6D4E8] whitespace-nowrap font-medium w-16 flex-shrink-0">{h.date}</span>
                  <span className="text-[#8fa3c0]">{h.event}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Link to="/app/my-cdp" className="mt-3 text-[11px] text-[#a78bfa] hover:text-white flex items-center gap-1 transition-colors font-medium">
          View full Career Development Plan <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

/* ═══ CTE PATHWAY (Career & Technical Education - Pinned) ═══ */
function CTEPathwayModule({ cte, target }) {
  const progressPct = Math.round((cte.courses_completed / Math.max(cte.courses_required, 1)) * 100);
  return (
    <div className="relative overflow-hidden rounded-2xl p-5 sm:p-6" data-testid="cte-pathway-module"
      style={{ background: 'rgba(6,23,54,0.72)', backdropFilter: 'blur(16px)', border: '1px solid rgba(57,227,220,0.22)',
        boxShadow: '0 0 6px rgba(57,227,220,0.35), 0 0 18px rgba(57,227,220,0.12)' }}>
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(57,227,220,0.45), transparent)' }} />

      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(57,227,220,0.12)', border: '1px solid rgba(57,227,220,0.25)', boxShadow: '0 0 12px rgba(57,227,220,0.15)' }}>
              <Shield className="w-5 h-5 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 4px rgba(57,227,220,0.5))' }} />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">CTE Pathway</h2>
              <p className="text-[10px] text-[#C6D4E8]">Career & Technical Education cluster</p>
            </div>
          </div>
          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold"
            style={{ background: cte.is_concentrator ? 'rgba(21,186,137,0.08)' : 'rgba(236,196,81,0.08)',
              border: `1px solid ${cte.is_concentrator ? 'rgba(21,186,137,0.3)' : 'rgba(236,196,81,0.3)'}`,
              color: cte.is_concentrator ? '#15BA89' : '#ECC451' }}>
            {cte.status}
          </span>
        </div>

        {/* Cluster + Pathway */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div className="p-3 rounded-lg" style={{ background: 'rgba(57,227,220,0.04)', border: '1px solid rgba(57,227,220,0.12)' }}>
            <div className="text-[9px] text-[#C6D4E8] font-semibold uppercase tracking-wider mb-1">Cluster</div>
            <div className="text-sm text-white font-bold">{cte.cluster}</div>
          </div>
          <div className="p-3 rounded-lg" style={{ background: 'rgba(120,47,225,0.04)', border: '1px solid rgba(120,47,225,0.12)' }}>
            <div className="text-[9px] text-[#C6D4E8] font-semibold uppercase tracking-wider mb-1">Pathway</div>
            <div className="text-sm text-white font-bold">{cte.pathway}</div>
          </div>
        </div>

        {/* Course progress */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-[#C6D4E8] font-medium">CTE Course Progress</span>
            <span className="text-white font-bold">{cte.courses_completed} of {cte.courses_required} courses</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(57,227,220,0.05)' }}>
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progressPct}%`, background: 'linear-gradient(90deg, #39E3DC, #15BA89)', boxShadow: '0 0 10px rgba(57,227,220,0.4)' }} />
          </div>
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap gap-2">
          {cte.is_concentrator && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ background: 'rgba(21,186,137,0.08)', border: '1px solid rgba(21,186,137,0.2)', color: '#15BA89' }}>
              CTE Concentrator
            </span>
          )}
          {cte.wbl_eligible && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ background: 'rgba(57,227,220,0.08)', border: '1px solid rgba(57,227,220,0.2)', color: '#39E3DC' }}>
              WBL Eligible
            </span>
          )}
          {cte.credential_ready && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ background: 'rgba(236,196,81,0.08)', border: '1px solid rgba(236,196,81,0.2)', color: '#ECC451' }}>
              Credential Ready
            </span>
          )}
        </div>

        <Link to="/app/nc-cte" className="mt-3 text-[11px] text-[#39E3DC] hover:text-white flex items-center gap-1 transition-colors font-medium">
          View full CTE pathway <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

/* ═══ FINANCIAL ═══ */
function FinancialModule() {
  const { token } = useAuth();
  const [fin, setFin] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/college/my-strategy`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const ls = r.data.life_strategy || {};
        const earn = r.data.earnings || {};
        setFin({ life: ls, earnings: earn, target: r.data.student?.career_target, lifestyle: r.data.student?.lifestyle_goal });
      }).catch(() => {});
  }, [token]);

  const startSalary = fin?.earnings?.trajectory?.[0] ? `$${fin.earnings.trajectory[0]}K` : '$55K';
  const peakSalary = fin?.earnings?.peak_label || '$120K';
  const target = fin?.target || 'your career';

  // Path comparison data
  const paths = [
    { label: '4-Year College', icon: '🎓', entry: startSalary, peak: peakSalary, cost: '$104K', time: '4 yrs', debt: '$28K', color: '#39E3DC' },
    { label: 'Military Service', icon: '🎖', entry: '$24K', peak: '$65K', cost: '$0', time: '4+ yrs', debt: '$0', color: '#a78bfa' },
    { label: 'Trade / Skilled', icon: '🔧', entry: '$38K', peak: '$75K', cost: '$15K', time: '2 yrs', debt: '$8K', color: '#ECC451' },
    { label: 'Direct Workforce', icon: '💼', entry: '$32K', peak: '$52K', cost: '$0', time: 'Now', debt: '$0', color: '#f87171' },
  ];

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(21,186,137,0.22)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 4px rgba(21,186,137,0.3), 0 0 14px rgba(21,186,137,0.1)' }}>
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="w-5 h-5 text-[#15BA89]" style={{ filter: 'drop-shadow(0 0 6px rgba(21,186,137,0.5))' }} />
        <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#15BA89]">Financial Intelligence</h2>
      </div>

      {/* Your path headline */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="p-3 rounded-lg" style={{ background: 'rgba(57,227,220,0.04)', border: '1px solid rgba(57,227,220,0.12)' }}>
          <div className="text-[9px] text-[#C6D4E8] font-semibold uppercase mb-0.5">Your Starting Salary</div>
          <div className="text-lg font-bold text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 6px rgba(57,227,220,0.4))' }}>{startSalary}</div>
          <div className="text-[9px] text-[#C6D4E8]">as {target}</div>
        </div>
        <div className="p-3 rounded-lg" style={{ background: 'rgba(21,186,137,0.04)', border: '1px solid rgba(21,186,137,0.12)' }}>
          <div className="text-[9px] text-[#C6D4E8] font-semibold uppercase mb-0.5">Peak Earning</div>
          <div className="text-lg font-bold text-[#15BA89]" style={{ filter: 'drop-shadow(0 0 6px rgba(21,186,137,0.4))' }}>{peakSalary}</div>
          <div className="text-[9px] text-[#C6D4E8]">10+ years in</div>
        </div>
      </div>

      {/* User-specific financial data */}
      <div className="space-y-1.5 mb-3">
        {fin?.life?.lifestyle && <div className="text-[10px] text-[#8fa3c0]">{fin.life.lifestyle}</div>}
        {fin?.life?.budget && <div className="text-[10px] text-[#8fa3c0]">{fin.life.budget}</div>}
        {fin?.life?.debt && <div className="text-[10px] text-[#8fa3c0]">{fin.life.debt}</div>}
      </div>

      {/* Path Comparison */}
      <div className="mt-3">
        <div className="text-[9px] text-[#C6D4E8] font-semibold uppercase tracking-wider mb-2">Compare Your Options</div>
        <div className="grid grid-cols-4 gap-2">
          {paths.map((p, i) => (
            <div key={i} className="p-2 rounded-lg text-center" style={{ background: `${p.color}06`, border: `1px solid ${p.color}18` }}>
              <div className="text-sm mb-1">{p.icon}</div>
              <div className="text-[9px] text-white font-semibold leading-tight mb-1.5">{p.label}</div>
              <div className="text-xs font-bold" style={{ color: p.color }}>{p.entry}</div>
              <div className="text-[8px] text-[#C6D4E8]">entry</div>
              <div className="text-[10px] font-bold text-white mt-1">{p.peak}</div>
              <div className="text-[8px] text-[#C6D4E8]">peak</div>
              <div className="text-[9px] text-[#C6D4E8] mt-1">Cost: {p.cost}</div>
              <div className="text-[9px] text-[#C6D4E8]">Time: {p.time}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3 mt-3">
        <Link to="/app/college-financial" className="text-[10px] text-[#39E3DC] hover:text-white flex items-center gap-1 transition-colors font-semibold">
          Full Financial Plan <ArrowRight className="w-3 h-3" />
        </Link>
        <Link to="/app/funding" className="text-[10px] text-[#15BA89] hover:text-white flex items-center gap-1 transition-colors font-semibold">
          Aid & FAFSA <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

/* ═══ AI IMPACT ═══ */
function AIImpactModule({ impact }) {
  const score = impact.task_exposure || impact.exposure_score || 0;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(236,196,81,0.22)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 40px -12px rgba(236,196,81,0.2)' }}>
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-5 h-5 text-[#ECC451]" style={{ filter: 'drop-shadow(0 0 6px rgba(236,196,81,0.5))' }} />
        <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#ECC451]">AI Career Impact</h2>
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div>
          <span className="text-5xl sm:text-6xl font-outfit font-black text-white" style={{ textShadow: '0 0 28px rgba(57,227,220,0.25)' }}>{score}%</span>
          <div className="text-[10px] text-slate-300 mt-0.5 font-medium">Task Exposure</div>
        </div>
        <p className="flex-1 text-xs sm:text-sm text-slate-300/80 leading-relaxed">{impact.what_it_means || impact.explanation}</p>
        <Link to="/app/future-of-work" className="text-xs text-[#39E3DC] hover:text-white flex items-center gap-1 transition-colors font-semibold flex-shrink-0">
          Full Intelligence Lab <ArrowRight className="w-3 h-3" /></Link>
      </div>
    </div>
  );
}

/* ═══ PROOF ═══ */
function ProofModule({ proof }) {
  const top = proof.proof_items.filter(p => p.priority === 'critical' || p.priority === 'high').slice(0, 3);
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(120,47,225,0.22)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 4px rgba(120,47,225,0.3), 0 0 14px rgba(120,47,225,0.1)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-[#a78bfa]" style={{ filter: 'drop-shadow(0 0 6px rgba(120,47,225,0.5))' }} />
          <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#a78bfa]">Proof Challenges</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {top.map(item => (
          <div key={item.id} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(120,47,225,0.15)' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded text-[#f43f5e]"
                style={{ background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.2)' }}>{item.priority}</span>
            </div>
            <h3 className="text-sm text-white font-semibold mb-1.5">{item.title}</h3>
            <p className="text-[10px] text-slate-300 leading-snug line-clamp-2">{item.why?.substring(0, 100)}</p>
            <Link to="/app/proof-builder" className="mt-2 text-[10px] text-[#39E3DC] flex items-center gap-1 font-semibold">Start building <ArrowRight className="w-3 h-3" /></Link>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ NEXT MOVES ═══ */
function NextMovesModule({ actions }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(236,196,81,0.18)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 4px rgba(236,196,81,0.2), 0 0 14px rgba(236,196,81,0.06)' }}>
      <div className="flex items-center gap-2 mb-4">
        <Star className="w-5 h-5 text-[#ECC451]" style={{ filter: 'drop-shadow(0 0 6px rgba(236,196,81,0.5))' }} />
        <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#ECC451]">Your Next Moves</h2>
      </div>
      <div className="space-y-2">
        {actions.slice(0, 4).map((a, i) => {
          const colors = ['#39E3DC', '#782FE1', '#15BA89', '#ECC451'];
          const c = colors[i];
          return (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg transition-all hover:bg-white/[0.02]" style={{ border: `1px solid ${c}18` }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold"
                style={{ background: `${c}12`, border: `1px solid ${c}30`, color: c, boxShadow: `0 0 8px ${c}15` }}>{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-semibold">{a.action}</div>
                <div className="text-[10px] text-slate-300 mt-0.5">{a.reason}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ READINESS ═══ */
function ReadinessModule({ radar, overall }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(57,227,220,0.22)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 4px rgba(57,227,220,0.3), 0 0 14px rgba(57,227,220,0.1)' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 6px rgba(57,227,220,0.5))' }} />
          <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#39E3DC]">Career Readiness</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-3xl font-extrabold text-white" style={{ textShadow: '0 0 20px rgba(57,227,220,0.25)' }}>{overall}%</span>
          <span className="text-[10px] text-slate-300">Overall</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {radar.slice(0, 6).map(dim => {
          const pct = Math.round((dim.score / dim.max) * 100);
          const color = pct >= 60 ? '#15BA89' : pct >= 40 ? '#39E3DC' : '#ECC451';
          return (
            <div key={dim.key}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-300 font-medium">{dim.label}</span>
                <span className="text-white font-bold">{pct}%</span>
              </div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: `${color}10` }}>
                <motion.div className="h-full rounded-full"
                  initial={{ width: 0 }} animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  style={{ background: `linear-gradient(90deg, ${color}, ${color}cc)`, boxShadow: `0 0 8px ${color}50, 0 0 16px ${color}20` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ LIFETIME EARNINGS MODULE (pinnable) ═══ */
function LifetimeEarningsModule() {
  const { token } = useAuth();
  const [earn, setEarn] = useState(null);
  const [target, setTarget] = useState('');
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/college/my-strategy`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setEarn(r.data.earnings); setTarget(r.data.student?.career_target || ''); }).catch(() => {});
  }, [token]);

  if (!earn) return null;
  const trajectory = earn.trajectory || [];
  const years = earn.years || [];
  const maxVal = Math.max(...trajectory, 1);
  const yearSpan = years.length;
  const totalK = trajectory.reduce((a, b) => a + b, 0);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(57,227,220,0.22)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 4px rgba(57,227,220,0.3), 0 0 14px rgba(57,227,220,0.1)' }}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 6px rgba(57,227,220,0.5))' }} />
          <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#39E3DC]">Lifetime Earnings</h2>
        </div>
        <div className="text-right">
          <span className="text-2xl font-extrabold text-[#39E3DC]" style={{ textShadow: '0 0 16px rgba(57,227,220,0.4)' }}>{earn.peak_label}</span>
          <div className="text-[9px] text-[#C6D4E8]">Peak annual</div>
        </div>
      </div>
      {target && <p className="text-[10px] text-[#C6D4E8] mb-3">Projected earnings as a <span className="text-white font-semibold">{target}</span></p>}

      {/* Mini bar chart */}
      <div className="flex items-end gap-[3px] mb-2" style={{ height: '72px' }}>
        {trajectory.map((val, i) => {
          const pct = Math.max((val / maxVal) * 100, 8);
          const isLast = i === trajectory.length - 1;
          return (
            <div key={i} className="flex-1 rounded-t-sm transition-all duration-500 relative group cursor-default"
              style={{
                height: `${pct}%`,
                background: isLast ? 'linear-gradient(180deg, #39E3DC, rgba(57,227,220,0.5))' : `linear-gradient(180deg, rgba(57,227,220,0.7), rgba(57,227,220,0.2))`,
                boxShadow: isLast ? '0 0 8px rgba(57,227,220,0.5)' : 'none',
                minHeight: '4px',
              }}>
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 hidden group-hover:block text-[8px] text-white bg-[#0a1a30] px-1.5 py-0.5 rounded whitespace-nowrap z-10 border border-[rgba(57,227,220,0.2)]">
                ${val}K
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[8px] text-[#C6D4E8]">
        <span>{years[0] || ''}</span>
        <span>{years[Math.floor(years.length / 2)] || ''}</span>
        <span>{years[years.length - 1] || ''}</span>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="text-[10px] text-[#C6D4E8]">
          {yearSpan}-year projected total: <span className="text-white font-bold">${(totalK / 1000).toFixed(1)}M</span>
        </div>
        <Link to="/app/future-outlook" className="text-[10px] text-[#39E3DC] hover:text-white flex items-center gap-1 transition-colors font-semibold">
          Full Projection <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

/* ═══ CAREER LANDSCAPE MODULE (pinnable) ═══ */
function CareerLandscapeModule({ target }) {
  const { token } = useAuth();
  const [careers, setCareers] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/college/career-intelligence`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const cp = r.data?.career_paths || {};
        const paths = [...(cp.direct_paths || []), ...(cp.stretch_paths || [])].slice(0, 4);
        setCareers(paths);
      }).catch(() => { setCareers([]); });
  }, [token]);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(120,47,225,0.22)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 4px rgba(120,47,225,0.3), 0 0 14px rgba(120,47,225,0.1)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Compass className="w-5 h-5 text-[#a78bfa]" style={{ filter: 'drop-shadow(0 0 6px rgba(120,47,225,0.5))' }} />
          <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#a78bfa]">Career Landscape</h2>
        </div>
      </div>

      {careers?.length > 0 ? (
        <div className="space-y-2">
          {careers.map((c, i) => {
            const colors = ['#39E3DC', '#a78bfa', '#15BA89', '#ECC451'];
            const clr = colors[i % colors.length];
            return (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: `${clr}06`, border: `1px solid ${clr}15` }}>
                <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ background: `${clr}15` }}>
                  <Briefcase className="w-3 h-3" style={{ color: clr }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white font-semibold truncate">{c.title || c.name}</div>
                  <div className="text-[9px] text-[#C6D4E8]">{c.median_salary || c.salary_range || ''}</div>
                </div>
                {c.growth && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: `${clr}12`, color: clr }}>{c.growth}</span>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-3">
          <p className="text-xs text-[#C6D4E8]">Loading careers for {target || 'your goal'}...</p>
        </div>
      )}

      <Link to="/app/career-landscape" className="mt-3 text-[10px] text-[#39E3DC] hover:text-white flex items-center gap-1 transition-colors font-semibold">
        Full Career Map <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

/* ═══ ASSESSMENT SUMMARY MODULE (pinnable) ═══ */
function AssessmentSummaryModule() {
  const { token } = useAuth();
  const [assess, setAssess] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/assessment/results`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAssess(r.data)).catch(() => {});
  }, [token]);

  const started = assess?.started;
  const complete = assess?.complete;
  const results = assess?.results || {};

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[rgba(236,196,81,0.22)] bg-[#061736] p-5"
      style={{ boxShadow: '0 0 4px rgba(236,196,81,0.25), 0 0 14px rgba(236,196,81,0.08)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-[#ECC451]" style={{ filter: 'drop-shadow(0 0 6px rgba(236,196,81,0.5))' }} />
          <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#ECC451]">Assessment Summary</h2>
        </div>
        {started && (
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: complete ? 'rgba(21,186,137,0.1)' : 'rgba(236,196,81,0.1)', border: `1px solid ${complete ? 'rgba(21,186,137,0.3)' : 'rgba(236,196,81,0.3)'}`, color: complete ? '#15BA89' : '#ECC451' }}>
            {complete ? 'Complete' : 'In Progress'}
          </span>
        )}
      </div>

      {started ? (
        <div className="space-y-3">
          {/* Top interests */}
          {results.interests?.top_types?.length > 0 ? (
            <div>
              <div className="text-[9px] text-[#C6D4E8] font-semibold uppercase mb-1.5">Top Interests</div>
              <div className="flex flex-wrap gap-1.5">
                {results.interests.top_types.slice(0, 3).map((t, i) => {
                  const colors = ['#39E3DC', '#a78bfa', '#15BA89'];
                  return (
                    <span key={i} className="px-2 py-1 rounded-full text-[10px] font-semibold"
                      style={{ background: `${colors[i]}10`, border: `1px solid ${colors[i]}25`, color: colors[i] }}>
                      {t.label || t.type || t}
                    </span>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-[#8fa3c0]">Assessment {complete ? 'complete' : 'in progress'}. {!complete && 'Complete all modules to see your full profile.'}</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Interests', done: !!results.interests, link: '/app/assessment?section=interests' },
                  { label: 'Personality', done: !!results.traits, link: '/app/assessment?section=traits' },
                  { label: 'Work Style', done: !!results.environment, link: '/app/assessment?section=environment' },
                  { label: 'Values', done: !!results.values, link: '/app/assessment?section=values' },
                ].map((mod, i) => (
                  <Link key={i} to={mod.link} className="flex items-center gap-2 p-2 rounded-lg transition-all hover:bg-white/[0.03]"
                    style={{ border: `1px solid ${mod.done ? 'rgba(21,186,137,0.2)' : 'rgba(236,196,81,0.15)'}` }}>
                    {mod.done
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-[#15BA89] flex-shrink-0" />
                      : <Circle className="w-3.5 h-3.5 text-[#C6D4E8] flex-shrink-0" />}
                    <span className={`text-[10px] font-medium ${mod.done ? 'text-[#8fa3c0]' : 'text-white'}`}>{mod.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Work environment */}
          {results.environment?.top_preferences?.length > 0 && (
            <div>
              <div className="text-[9px] text-[#C6D4E8] font-semibold uppercase mb-1.5">Work Style</div>
              <div className="flex flex-wrap gap-1.5">
                {results.environment.top_preferences.slice(0, 3).map((p, i) => (
                  <span key={i} className="px-2 py-1 rounded-full text-[10px] font-medium" style={{ background: 'rgba(120,47,225,0.08)', border: '1px solid rgba(120,47,225,0.2)', color: '#a78bfa' }}>
                    {p.label || p}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Top values */}
          {results.values?.top_values?.length > 0 && (
            <div>
              <div className="text-[9px] text-[#C6D4E8] font-semibold uppercase mb-1.5">Core Values</div>
              <div className="flex flex-wrap gap-1.5">
                {results.values.top_values.slice(0, 3).map((v, i) => (
                  <span key={i} className="px-2 py-1 rounded-full text-[10px] font-medium" style={{ background: 'rgba(21,186,137,0.08)', border: '1px solid rgba(21,186,137,0.2)', color: '#15BA89' }}>
                    {v.label || v}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-sm text-[#C6D4E8] mb-3">Take the Career Fit Assessment to unlock personalized insights.</p>
          <Link to="/app/assessment" className="px-4 py-2 rounded-lg text-xs font-bold text-white inline-flex items-center gap-1.5"
            style={{ background: 'linear-gradient(90deg, #352CA0, #782FE1)', boxShadow: '0 0 1px rgba(120,47,225,0.8), 0 0 16px rgba(120,47,225,0.3)' }}>
            Start Assessment <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}

      {started && (
        <Link to="/app/assessment?view=intro" className="mt-3 text-[10px] text-[#39E3DC] hover:text-white flex items-center gap-1 transition-colors font-semibold">
          View Full Results <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

